import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardActions,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  LinearProgress,
  CircularProgress,
  Skeleton,
  Tooltip
} from '@mui/material'
import {
  Download as DownloadIcon,
  Assessment as ReportIcon,
  TableChart as TableIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  TrendingUp as TrendingUpIcon,
  Description as DescriptionIcon
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useAuth } from '../../../../auth/useAuth'

interface ReportTemplate {
  id: string
  name: string
  description: string
  type: 'funds' | 'positions' | 'accounts' | 'summary'
  icon: React.ReactNode
  fields: string[]
}

interface ExportRequest {
  template: string
  dateFrom?: Date
  dateTo?: Date
  includeDeleted: boolean
  latestOnly?: boolean
  format: 'xlsx' | 'csv'
}

const ReportsManagement: React.FC = () => {
  const { user, getAccessToken } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [exportProgress, setExportProgress] = useState(0)
  
  // Export state
  const [selectedTemplate, setSelectedTemplate] = useState('')
  const [dateFrom, setDateFrom] = useState<Date | null>(null)
  const [dateTo, setDateTo] = useState<Date | null>(null)
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [latestOnly, setLatestOnly] = useState(() => {
    const userEmail = (user as any)?.email || (user as any)?.preferred_username || (user as any)?.username || 'default'
    const saved = localStorage.getItem(`exportConfig_latestOnly_${userEmail}`)
    return saved === 'true'
  })
  const [exportFormat, setExportFormat] = useState<'xlsx' | 'csv'>('xlsx')

  // Persist latestOnly preference per user
  useEffect(() => {
    const userEmail = (user as any)?.email || (user as any)?.preferred_username || (user as any)?.username || 'default'
    localStorage.setItem(`exportConfig_latestOnly_${userEmail}`, String(latestOnly))
  }, [latestOnly, user])

  // Report templates - matching original R system exactly (2 reports)
  const reportTemplates: ReportTemplate[] = [
    {
      id: 'comprehensive_report',
      name: 'Comprehensive Excel Report',
      description: 'Multi-sheet Excel report with deals, strategy, geography, industry, collateral, fund, and seniority analysis (matches original R "Download Excel")',
      type: 'summary',
      icon: <ReportIcon />,
      fields: [
        'Sheet 1: Deals - All position details with fund and account data',
        'Sheet 2: Strategy - Breakdown by investment strategy',
        'Sheet 3: Geography - Breakdown by geographic region',
        'Sheet 4: Industry - Breakdown by industry sector',
        'Sheet 5: Collateral - Breakdown by collateral type',
        'Sheet 6: Fund - Breakdown by fund',
        'Sheet 7: Seniority - Breakdown by position seniority',
        'Sheet 8: Industry Level 1 - High-level industry groupings'
      ]
    },
    {
      id: 'raw_data_export',
      name: 'Raw Data Export',
      description: 'Complete database dump of all tables: Funds, Accounts, Positions, and Identifiers (matches original R "Dump All SQL Data")',
      type: 'summary',
      icon: <TableIcon />,
      fields: [
        'All Funds table data',
        'All Accounts table data', 
        'All Positions table data',
        'All Identifiers table data',
        'Raw database format with all columns'
      ]
    }
  ]

  // Handle export
  const handleExport = async () => {
    if (!selectedTemplate) {
      setError('Please select a report template')
      return
    }

    setLoading(true)
    setError(null)
    setSuccess(null)
    setExportProgress(0)

    try {
      const exportRequest: ExportRequest = {
        template: selectedTemplate,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        includeDeleted,
        latestOnly,
        format: exportFormat
      }

      // Simulate progress
      const progressInterval = setInterval(() => {
        setExportProgress(prev => Math.min(prev + 10, 90))
      }, 200)

      const token = await getAccessToken()
      
      if (!token) {
        throw new Error('No access token available')
      }

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/reports/export`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(exportRequest)
      })

      clearInterval(progressInterval)
      setExportProgress(100)

      if (!response.ok) {
        throw new Error('Failed to generate report')
      }

      // Handle file download
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      
      const template = reportTemplates.find(t => t.id === selectedTemplate)
      const timestamp = new Date().toISOString().split('T')[0]
      a.download = `${template?.name.replace(/\s+/g, '_')}_${timestamp}.${exportFormat}`
      
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      setSuccess('Report exported successfully')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed')
      // For development, simulate successful export
      setTimeout(() => {
        setSuccess('Report export simulated successfully (development mode)')
        setExportProgress(100)
      }, 1000)
    } finally {
      setLoading(false)
      setTimeout(() => setExportProgress(0), 2000)
    }
  }

  // Generate quick report
  const handleQuickReport = async (templateId: string) => {
    setSelectedTemplate(templateId)
    setDateFrom(null)
    setDateTo(null)
    setIncludeDeleted(false)
    setExportFormat('xlsx')
    
    // Trigger export directly with the template ID
    await handleExportWithTemplate(templateId)
  }

  // Handle export with specific template
  const handleExportWithTemplate = async (templateId: string) => {
    setLoading(true)
    setError(null)
    setSuccess(null)
    setExportProgress(0)

    try {
      const exportRequest = {
        template: templateId,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        includeDeleted,
        latestOnly,
        format: exportFormat
      }

      // Simulate progress
      const progressInterval = setInterval(() => {
        setExportProgress(prev => Math.min(prev + 10, 90))
      }, 200)

      const token = await getAccessToken()
      
      if (!token) {
        throw new Error('No access token available')
      }

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/reports/export`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(exportRequest)
      })

      clearInterval(progressInterval)
      setExportProgress(100)

      if (!response.ok) {
        throw new Error('Failed to generate report')
      }

      // Handle file download
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      
      const template = reportTemplates.find(t => t.id === templateId)
      const timestamp = new Date().toISOString().split('T')[0]
      a.download = `${template?.name.replace(/\s+/g, '_')}_${timestamp}.${exportFormat}`
      
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      setSuccess('Report exported successfully')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed')
      // For development, simulate successful export
      setTimeout(() => {
        setSuccess('Report export simulated successfully (development mode)')
        setExportProgress(100)
      }, 1000)
    } finally {
      setLoading(false)
      setTimeout(() => setExportProgress(0), 2000)
    }
  }

  const selectedTemplateData = reportTemplates.find(t => t.id === selectedTemplate)

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
          Reports & Analytics
        </Typography>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Export Progress */}
        {loading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Generating report... {exportProgress}%
            </Typography>
            <LinearProgress variant="determinate" value={exportProgress} />
          </Box>
        )}

        <Grid container spacing={3}>
          {/* Report Templates */}
          <Grid item xs={12} lg={8}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Available Reports
            </Typography>
            
            <Grid container spacing={2}>
              {reportTemplates.map((template) => (
                <Grid item xs={12} md={6} key={template.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {template.icon}
                        <Typography variant="h6" sx={{ ml: 1, fontWeight: 'bold' }}>
                          {template.name}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {template.description}
                      </Typography>
                      
                      <Chip 
                        label={template.type.toUpperCase()} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />
                      
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Included Fields:
                      </Typography>
                      <List dense>
                        {template.fields.slice(0, 4).map((field, index) => (
                          <ListItem key={index} sx={{ py: 0, px: 0 }}>
                            <ListItemText 
                              primary={field} 
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                        {template.fields.length > 4 && (
                          <ListItem sx={{ py: 0, px: 0 }}>
                            <Tooltip
                              title={
                                <Box>
                                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                    All Fields in {template.name}:
                                  </Typography>
                                  {template.fields.map((field, index) => (
                                    <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                                      • {field}
                                    </Typography>
                                  ))}
                                </Box>
                              }
                              arrow
                              placement="top"
                              sx={{
                                '& .MuiTooltip-tooltip': {
                                  maxWidth: 400,
                                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                  fontSize: '0.875rem'
                                }
                              }}
                            >
                              <ListItemText 
                                primary={`... and ${template.fields.length - 4} more fields (hover to see all)`}
                                primaryTypographyProps={{ 
                                  variant: 'body2', 
                                  fontStyle: 'italic',
                                  sx: { 
                                    cursor: 'help',
                                    textDecoration: 'underline',
                                    textDecorationStyle: 'dotted',
                                    '&:hover': {
                                      color: 'primary.main'
                                    }
                                  }
                                }}
                              />
                            </Tooltip>
                          </ListItem>
                        )}
                      </List>
                    </CardContent>
                    
                    <CardActions>
                      <Button
                        size="small"
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                        onClick={() => handleQuickReport(template.id)}
                        disabled={loading}
                      >
                        {loading ? 'Exporting...' : 'Quick Export'}
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setSelectedTemplate(template.id)}
                        disabled={loading}
                      >
                        Customize
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Export Configuration */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ position: 'sticky', top: 20 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 1.5 }}>
                  Export Configuration
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Report Template</InputLabel>
                    <Select
                      value={selectedTemplate}
                      onChange={(e) => setSelectedTemplate(e.target.value)}
                      label="Report Template"
                    >
                      {reportTemplates.map(template => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <DatePicker
                    label="Date From (Optional)"
                    value={dateFrom}
                    onChange={(date) => setDateFrom(date)}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />

                  <DatePicker
                    label="Date To (Optional)"
                    value={dateTo}
                    onChange={(date) => setDateTo(date)}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />

                  {/* Original R system only supported Excel format - no CSV option */}
                  <FormControl fullWidth size="small">
                    <InputLabel>Export Format</InputLabel>
                    <Select
                      value={exportFormat}
                      onChange={(e) => setExportFormat(e.target.value as 'xlsx' | 'csv')}
                      label="Export Format"
                      disabled
                    >
                      <MenuItem value="xlsx">Excel (.xlsx) - Original R Format</MenuItem>
                    </Select>
                  </FormControl>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                      type="checkbox"
                      id="includeDeleted"
                      checked={includeDeleted}
                      onChange={(e) => setIncludeDeleted(e.target.checked)}
                    />
                    <label htmlFor="includeDeleted">
                      <Typography variant="body2">
                        Include deleted records
                      </Typography>
                    </label>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                      type="checkbox"
                      id="latestOnly"
                      checked={latestOnly}
                      onChange={(e) => setLatestOnly(e.target.checked)}
                    />
                    <label htmlFor="latestOnly">
                      <Typography variant="body2">
                        Latest timestamp only (per position/fund/account)
                      </Typography>
                    </label>
                  </Box>

                  {selectedTemplateData && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Selected Template:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedTemplateData.description}
                      </Typography>
                      <Chip 
                        label={`${selectedTemplateData.fields.length} fields`}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                    onClick={handleExport}
                    disabled={loading || !selectedTemplate}
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    {loading ? 'Generating...' : 'Export Report'}
                  </Button>
                </Box>
              </Paper>

              {/* Quick Stats */}
              <Paper sx={{ p: 2, mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 1.5 }}>
                  Quick Statistics
                </Typography>
                
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <ReportIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Available Templates" 
                      secondary={`${reportTemplates.length} report types`}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemIcon>
                      <DownloadIcon color="success" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Export Formats" 
                      secondary="Excel, CSV supported"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemIcon>
                      <TrendingUpIcon color="info" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Data Sources" 
                      secondary="Funds, Positions, Accounts"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  )
}

export default ReportsManagement
